import * as XLSX from 'xlsx';
import { CreateB2BProductInput } from '@/types/database/b2b-products';

interface ParsedB2BProduct {
  data: CreateB2BProductInput[];
  errors: string[];
}

export const parseB2BProductsFromExcel = (workbook: XLSX.WorkBook): ParsedB2BProduct => {
  const errors: string[] = [];
  const data: CreateB2BProductInput[] = [];

  try {
    // Get the first worksheet
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      errors.push('No worksheets found in the file');
      return { data, errors };
    }

    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (jsonData.length < 2) {
      errors.push('File must contain at least a header row and one data row');
      return { data, errors };
    }

    // Get headers from first row
    const headers = jsonData[0] as string[];
    const requiredColumns = ['Name', 'Price', 'Unit'];
    
    // Check for required columns
    const missingColumns = requiredColumns.filter(col => 
      !headers.some(header => header?.toLowerCase().includes(col.toLowerCase()))
    );

    if (missingColumns.length > 0) {
      errors.push(`Missing required columns: ${missingColumns.join(', ')}`);
      return { data, errors };
    }

    // Find column indices
    const nameIndex = headers.findIndex(h => h?.toLowerCase().includes('name'));
    const priceIndex = headers.findIndex(h => h?.toLowerCase().includes('price'));
    const unitIndex = headers.findIndex(h => h?.toLowerCase().includes('unit'));
    const descriptionIndex = headers.findIndex(h => h?.toLowerCase().includes('description'));
    const tagsIndex = headers.findIndex(h => h?.toLowerCase().includes('tags'));
    const weightIndex = headers.findIndex(h => h?.toLowerCase().includes('weight'));
    const purchasePriceIndex = headers.findIndex(h => h?.toLowerCase().includes('purchase') && h?.toLowerCase().includes('price'));
    const spoilageRateIndex = headers.findIndex(h => h?.toLowerCase().includes('spoilage'));
    const costNotesIndex = headers.findIndex(h => h?.toLowerCase().includes('cost') && h?.toLowerCase().includes('notes'));

    // Process data rows
    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i] as any[];
      
      if (!row || row.length === 0) continue;

      const name = row[nameIndex]?.toString().trim();
      const priceStr = row[priceIndex]?.toString().trim();
      const unit = row[unitIndex]?.toString().trim() || 'kg';

      if (!name) {
        errors.push(`Row ${i + 1}: Product name is required`);
        continue;
      }

      if (!priceStr) {
        errors.push(`Row ${i + 1}: Price is required`);
        continue;
      }

      const price = parseFloat(priceStr);
      if (isNaN(price) || price < 0) {
        errors.push(`Row ${i + 1}: Invalid price value: ${priceStr}`);
        continue;
      }

      const product: CreateB2BProductInput = {
        product_name: name,
        price,
        unit,
        status: 'active'
      };

      // Optional fields
      if (descriptionIndex >= 0 && row[descriptionIndex]) {
        product.short_description = row[descriptionIndex].toString().trim();
      }

      if (tagsIndex >= 0 && row[tagsIndex]) {
        const tagsStr = row[tagsIndex].toString().trim();
        if (tagsStr) {
          product.tags = tagsStr.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
        }
      }

      if (weightIndex >= 0 && row[weightIndex]) {
        const weight = parseFloat(row[weightIndex].toString());
        if (!isNaN(weight) && weight > 0) {
          product.weight = weight;
        }
      }

      if (purchasePriceIndex >= 0 && row[purchasePriceIndex]) {
        const purchasePrice = parseFloat(row[purchasePriceIndex].toString());
        if (!isNaN(purchasePrice) && purchasePrice >= 0) {
          product.purchase_price_per_kg = purchasePrice;
        }
      }

      if (spoilageRateIndex >= 0 && row[spoilageRateIndex]) {
        const spoilageRate = parseFloat(row[spoilageRateIndex].toString());
        if (!isNaN(spoilageRate) && spoilageRate >= 0 && spoilageRate <= 100) {
          product.spoilage_rate = spoilageRate;
        }
      }

      if (costNotesIndex >= 0 && row[costNotesIndex]) {
        product.cost_notes = row[costNotesIndex].toString().trim();
      }

      data.push(product);
    }

    if (data.length === 0 && errors.length === 0) {
      errors.push('No valid product data found in the file');
    }

  } catch (error) {
    console.error('Error parsing B2B products Excel file:', error);
    errors.push(`Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return { data, errors };
};